import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Carregando from "./Carregando";
import { dataHora, dolar, frase, lerAlertas, lerConsumo, plural, type Alertas, type Consumo, type RefAlvo, type Resposta } from "../lib/sentinela";
import { DonoDoAlvo, Numero, SeloSeveridade } from "./SentinelaUi";

/**
 * Os alertas (a tela `/alerts` de lá) e o consumo de API (a `/logs`, que lá é só do administrador), SÓ LEITURA.
 * "Marcar como lido" é escrita e fica para a fase 3b. Os números do consumo são do banco, sobre TODAS as
 * chamadas, com o custo sem estimativa contado à parte (a conta do `my_api_cost` de lá); lá as fontes eram
 * contadas sobre as 500 linhas da lista.
 */

function LinkDoAlvo({ alvo, base }: { alvo: RefAlvo; base: string }) {
  if (!alvo) return <span className="text-xs text-cinza">sem alvo</span>;
  return (
    <Link to={`${base}?${alvo.tipo}=${alvo.id}`} className="text-sm font-semibold text-link underline" data-link-alvo={alvo.id}>
      {alvo.tipo === "empresa" ? "Ver terceiro" : "Ver pessoa"}: {alvo.nome || "—"}
    </Link>
  );
}

export function AlertasDoSentinela({ base }: { base: string }) {
  const [r, setR] = useState<Alertas | Resposta | null>(null);
  useEffect(() => {
    let vivo = true;
    void lerAlertas().then((x) => vivo && setR(x));
    return () => {
      vivo = false;
    };
  }, []);

  if (!r) return <Carregando texto="Lendo os alertas…" />;
  if (!r.ok) return <p className="surface rounded-cf p-5 text-sm text-tinta">{frase(r)}</p>;

  return (
    <section className="flex flex-col gap-4" data-sentinela-alertas data-total={r.total} data-nao-lidos={r.nao_lidos}>
      <div>
        <h3 className="font-display text-xl font-bold text-titulo">Alertas de risco</h3>
        <p className="mt-1 text-sm text-cinza">
          Mudanças de situação detectadas nas varreduras: {plural(r.total, "alerta", "alertas")}, {r.nao_lidos} não{" "}
          {r.nao_lidos === 1 ? "lido" : "lidos"}.
          {r.total > r.alertas.length && ` A lista mostra os ${r.alertas.length} mais recentes.`}
        </p>
      </div>
      {r.alertas.length === 0 ? (
        <p className="surface rounded-cf p-5 text-sm text-cinza">Nenhum alerta no momento.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {r.alertas.map((a) => (
            <li
              key={a.id}
              className={`surface rounded-cf border-l-4 p-4 [overflow-wrap:anywhere] ${a.lido ? "border-l-borda" : "border-l-erro-tinta"}`}
              data-alerta={a.id}
              data-lido={a.lido ? "" : undefined}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-titulo">{a.titulo}</span>
                <SeloSeveridade valor={a.severidade} />
                <span className="text-xs font-semibold text-cinza">{a.lido ? "lido" : "não lido"}</span>
              </div>
              {a.mensagem && <p className="mt-1 text-sm text-tinta">{a.mensagem}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cinza">
                <span>{dataHora(a.criado_em)}</span>
                <span className={a.email_enviado ? "text-aviso-tinta" : "text-alerta-tinta"}>
                  {a.email_enviado
                    ? `E-mail enviado${a.email_enviado_em ? ` em ${dataHora(a.email_enviado_em)}` : ""}`
                    : `E-mail não enviado${a.email_erro ? `: ${a.email_erro}` : "."}`}
                </span>
                <DonoDoAlvo dono={a.dono} />
              </div>
              <div className="mt-2">
                <LinkDoAlvo alvo={a.alvo} base={base} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A lista mostra 50 por vez: as 500 de uma vez davam uma página de mais de 20 mil pixels. */
const POR_VEZ = 50;

export function ConsumoDoSentinela({ base }: { base: string }) {
  const [r, setR] = useState<Consumo | Resposta | null>(null);
  const [quantas, setQuantas] = useState(POR_VEZ);
  useEffect(() => {
    let vivo = true;
    void lerConsumo().then((x) => vivo && setR(x));
    return () => {
      vivo = false;
    };
  }, []);

  if (!r) return <Carregando texto="Lendo o consumo…" />;
  if (!r.ok) return <p className="surface rounded-cf p-5 text-sm text-tinta">{frase(r)}</p>;
  const n = r.numeros;

  return (
    <section className="flex flex-col gap-4" data-sentinela-consumo>
      <div>
        <h3 className="font-display text-xl font-bold text-titulo">Consumo de API</h3>
        <p className="mt-1 text-sm text-cinza">
          As consultas às fontes e o custo estimado das chamadas pagas{n.desde ? `, desde ${dataHora(n.desde)}` : ""}.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Numero rotulo="Consultas" valor={n.consultas} atributo="consultas" />
        <Numero rotulo="Bem-sucedidas" valor={`${n.sucesso.toLocaleString("pt-BR")}/${n.consultas.toLocaleString("pt-BR")}`} atributo="sucesso" />
        <Numero rotulo={`Custo total (${plural(n.pagas, "chamada paga", "chamadas pagas")})`} valor={dolar(n.total_usd)} atributo="custo" />
        <Numero rotulo="Fontes distintas" valor={n.fontes} atributo="fontes" />
      </div>
      {n.sem_estimativa > 0 && (
        <p role="note" className="rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-4 py-2 text-sm text-alerta-tinta" data-sem-estimativa>
          <strong>{plural(n.sem_estimativa, "chamada", "chamadas")} sem estimativa de custo:</strong> o custo real é maior que o total
          acima. Elas aparecem na lista com «?» no lugar do valor.
        </p>
      )}
      <ul className="flex flex-wrap gap-2" data-por-fonte>
        {r.por_fonte.map((f) => (
          <li key={f.fonte} className="rounded-full border border-borda px-3 py-1 text-xs text-tinta">
            {f.fonte}: <strong>{f.consultas.toLocaleString("pt-BR")}</strong>
            {f.custo > 0 ? ` · ${dolar(f.custo)}` : ""}
          </li>
        ))}
      </ul>
      {r.chamadas.length === 0 ? (
        <p className="surface rounded-cf p-5 text-sm text-cinza">Nenhuma consulta registrada ainda.</p>
      ) : (
        <>
          <p className="text-xs text-cinza">
            {Math.min(quantas, r.chamadas.length).toLocaleString("pt-BR")} das {r.chamadas.length.toLocaleString("pt-BR")} chamadas mais recentes
            {n.consultas > r.chamadas.length ? ` (de ${n.consultas.toLocaleString("pt-BR")} no total)` : ""}.
          </p>
          <ul className="surface divide-y divide-borda rounded-cf" data-chamadas>
            {r.chamadas.slice(0, quantas).map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm" data-chamada>
                {/* O alvo consultado do julgamento da mídia é o endereço do documento: 200 caracteres sem espaço, que
                    no celular estouravam a largura (achado pelo sabotador de tela, com a lista inteira aberta). */}
                <div className="min-w-0 [overflow-wrap:anywhere]">
                  <span className={`mr-2 text-xs font-bold ${c.sucesso ? "text-aviso-tinta" : "text-erro-tinta"}`}>{c.sucesso ? "ok" : "falhou"}</span>
                  <span className="font-semibold text-titulo">{c.fonte}</span>
                  {c.endpoint && <code className="ml-2 rounded bg-chip px-1.5 py-0.5 text-xs text-tinta">{c.endpoint}</code>}
                  {c.alvo_consultado && <span className="text-cinza"> · {c.alvo_consultado}</span>}
                  <div className="text-xs text-cinza">
                    {dataHora(c.criado_em)}
                    {c.alvo && (
                      <>
                        {" · "}
                        <Link to={`${base}?${c.alvo.tipo}=${c.alvo.id}`} className="text-link underline">
                          {c.alvo.nome || "—"}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs text-cinza">
                  {c.status_http ?? "—"} · {c.duracao_ms ?? 0} ms · <span className="font-semibold text-tinta">{dolar(c.custo_estimado)}</span>
                </div>
              </li>
            ))}
          </ul>
          {quantas < r.chamadas.length && (
            <button
              type="button"
              onClick={() => setQuantas((q) => q + POR_VEZ)}
              className="min-h-10 self-start rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-acento"
              data-mostrar-mais
            >
              Mostrar mais {Math.min(POR_VEZ, r.chamadas.length - quantas)}
            </button>
          )}
        </>
      )}
    </section>
  );
}
